document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('[data-site-header]');
  const menuButton = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');

  const closeMenu = () => {
    if (!menuButton || !menu) return;
    menu.hidden = true;
    menuButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
  };

  menuButton?.addEventListener('click', () => {
    const isOpen = menuButton.getAttribute('aria-expanded') === 'true';
    if (header && menu) menu.style.setProperty('--mobile-menu-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    menuButton.setAttribute('aria-expanded', String(!isOpen));
    menu.hidden = isOpen;
    document.body.classList.toggle('menu-open', !isOpen);
  });

  menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { closeMenu(); menuButton?.focus(); } });
  window.addEventListener('resize', () => { if (window.innerWidth >= 900) closeMenu(); });
  window.addEventListener('scroll', () => header?.classList.toggle('is-scrolled', window.scrollY > 12), { passive: true });

  let cartUpdateTimer;

  const setCartBusy = (form, busy) => {
    form.toggleAttribute('aria-busy', busy);
    form.querySelectorAll('[data-cart-quantity-step]').forEach((button) => {
      button.disabled = busy;
    });
    form.querySelectorAll('[data-cart-quantity-input]').forEach((input) => {
      input.readOnly = busy;
    });
  };

  const submitNativeCartUpdate = (form) => {
    const submitter = form.querySelector('[data-cart-update-submitter]');
    if (!submitter) return;
    setCartBusy(form, true);
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      form.submit();
    }
  };

  const queueCartUpdate = (input, delay = 500) => {
    const quantity = Number(input.value);
    if (input.value === '' || !Number.isInteger(quantity) || quantity < 0) {
      input.setAttribute('aria-invalid', 'true');
      return;
    }

    input.removeAttribute('aria-invalid');
    input.value = String(quantity);
    const form = input.closest('[data-cart-form]');
    if (!form) return;
    window.clearTimeout(cartUpdateTimer);
    cartUpdateTimer = window.setTimeout(() => submitNativeCartUpdate(form), delay);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-quantity-step]');
    if (!button || button.disabled) return;
    const stepper = button.closest('.cart-stepper');
    const input = stepper?.querySelector('[data-cart-quantity-input]');
    if (!input) return;

    event.preventDefault();
    const current = Number(input.value) || 0;
    const step = Number(button.dataset.cartQuantityStep) || 0;
    input.value = String(Math.max(0, current + step));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-step]');
    if (!button || button.disabled) return;
    const control = button.closest('[data-quantity]');
    const input = control?.querySelector('input[name="quantity"]');
    if (!input) return;

    event.preventDefault();
    const minimum = Number(input.min) || 1;
    const current = Number(input.value) || minimum;
    const step = Number(button.dataset.quantityStep) || 0;
    input.value = String(Math.max(minimum, current + step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const handleCartQuantityChange = (event) => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (input) queueCartUpdate(input);
  };

  document.addEventListener('input', handleCartQuantityChange);
  document.addEventListener('change', handleCartQuantityChange);
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-cart-form]');
    if (!form) return;
    window.clearTimeout(cartUpdateTimer);
    setCartBusy(form, true);
  });

  const initProductGallery = (gallery) => {
    if (gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';
    const main = gallery.querySelector('[data-product-main-image]');
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    if (!main || !thumbs.length) return;

    thumbs.forEach((button, index) => {
      button.toggleAttribute('aria-current', index === 0);
      button.addEventListener('click', () => {
        const image = button.querySelector('img');
        if (!image || !button.dataset.largeSrc) return;
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = button.dataset.largeSrc;
        main.alt = image.alt;
        thumbs.forEach((thumb) => thumb.removeAttribute('aria-current'));
        button.setAttribute('aria-current', 'true');
      });
    });
  };

  const initProductGalleries = (root = document) => {
    const galleries = root.matches?.('[data-product-gallery]')
      ? [root]
      : [...root.querySelectorAll('[data-product-gallery]')];
    galleries.forEach(initProductGallery);
  };

  initProductGalleries();
  document.addEventListener('shopify:section:load', (event) => initProductGalleries(event.target));

  document.querySelectorAll('[data-variant-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      const price = document.querySelector('[data-product-price]');
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
    });
  });

  const initStoryCarousel = (carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;
    carousel.dataset.carouselReady = 'true';

    const slides = [...carousel.querySelectorAll('[data-story-slide]')];
    const previous = carousel.querySelector('[data-story-previous]');
    const next = carousel.querySelector('[data-story-next]');
    const controls = carousel.querySelector('[data-story-controls]');

    if (slides.length < 2) {
      if (controls) controls.hidden = true;
      return;
    }

    let current = 0;
    let timer;
    let isHovered = false;
    let hasFocus = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Math.max(3000, Number(carousel.dataset.autoplayMs) || 5000);

    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === current;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
    };

    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (reducedMotion || isHovered || hasFocus) return;
      timer = window.setInterval(() => show(current + 1), interval);
    };

    previous?.addEventListener('click', () => {
      show(current - 1);
      start();
    });
    next?.addEventListener('click', () => {
      show(current + 1);
      start();
    });
    carousel.addEventListener('mouseenter', () => {
      isHovered = true;
      stop();
    });
    carousel.addEventListener('mouseleave', () => {
      isHovered = false;
      start();
    });
    carousel.addEventListener('focusin', () => {
      hasFocus = true;
      stop();
    });
    carousel.addEventListener('focusout', (event) => {
      if (carousel.contains(event.relatedTarget)) return;
      hasFocus = false;
      start();
    });

    carousel.storyCarouselStop = stop;
    show(0);
    start();
  };

  const initStoryCarousels = (root = document) => {
    const carousels = root.matches?.('[data-story-carousel]')
      ? [root]
      : [...root.querySelectorAll('[data-story-carousel]')];
    carousels.forEach(initStoryCarousel);
  };

  initStoryCarousels();
  document.addEventListener('shopify:section:load', (event) => initStoryCarousels(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-story-carousel]')?.storyCarouselStop?.();
  });
});
